import { HumanFriendlySchedule } from '../types';

/**
 * Converts a cron expression back to a HumanFriendlySchedule object
 * This is a reverse operation of the ScheduleConverterService
 * @param cronExpression - The cron expression (e.g., "0 14 * * *")
 * @returns A HumanFriendlySchedule object
 */
export function cronToHumanFriendly(cronExpression: string): HumanFriendlySchedule {
  if (!cronExpression || cronExpression.trim() === '') {
    return {
      frequency: 'Daily',
      dailyInterval: 1,
      dailyTime: '02:00',
    };
  }

  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length !== 5) {
    // Default fallback
    return {
      frequency: 'Daily',
      dailyInterval: 1,
      dailyTime: '02:00',
    };
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Helper to format time as HH:MM
  const formatTime = (h: string, m: string): string => {
    const hourNum = parseInt(h, 10);
    const minuteNum = parseInt(m, 10);
    if (isNaN(hourNum) || isNaN(minuteNum)) {
      return '02:00';
    }
    const hourStr = hourNum.toString().padStart(2, '0');
    const minuteStr = minuteNum.toString().padStart(2, '0');
    return `${hourStr}:${minuteStr}`;
  };

  const timeStr = formatTime(hour, minute);

  // Check for one-time schedule (specific date)
  if (dayOfMonth !== '*' && month !== '*' && dayOfWeek === '*') {
    // This could be a one-time or yearly schedule
    // For now, treat as yearly if month is not *
    if (month !== '*') {
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(dayOfMonth, 10);
      if (!isNaN(monthNum) && !isNaN(dayNum)) {
        return {
          frequency: 'Yearly',
          yearlyMonth: monthNum,
          yearlyType: 'DayOfMonth',
          yearlyDay: dayNum,
          yearlyTime: timeStr,
        };
      }
    }
  }

  // Check for daily schedule: * * * * *
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    // Check for "Occurs Every" pattern: minute startHour-endHour/interval * * *
    // Example: "0 8-18/2 * * *" = every 2 hours from 8 AM to 6 PM
    if (hour.includes('-') && hour.includes('/')) {
      const [range, interval] = hour.split('/');
      const [startHour, endHour] = range.split('-');
      const startHourNum = parseInt(startHour, 10);
      const endHourNum = parseInt(endHour, 10);
      const intervalNum = parseInt(interval, 10);
      
      if (!isNaN(startHourNum) && !isNaN(endHourNum) && !isNaN(intervalNum)) {
        const startTime = `${startHourNum.toString().padStart(2, '0')}:${minute.padStart(2, '0')}`;
        const endTime = `${endHourNum.toString().padStart(2, '0')}:${minute.padStart(2, '0')}`;
        return {
          frequency: 'Daily',
          dailyInterval: 1,
          dailyFrequencyType: 'OccursEvery',
          dailyOccursEveryInterval: intervalNum,
          dailyOccursEveryUnit: 'hours',
          dailyOccursEveryStartTime: startTime,
          dailyOccursEveryEndTime: endTime,
        };
      }
    }
    
    // Check for "Occurs Every" pattern with minutes: */interval startHour-endHour * * *
    // Example: "*/30 8-18 * * *" = every 30 minutes from 8 AM to 6 PM
    if (minute.startsWith('*/') && hour.includes('-') && !hour.includes('/')) {
      const minuteInterval = parseInt(minute.substring(2), 10);
      const [startHour, endHour] = hour.split('-');
      const startHourNum = parseInt(startHour, 10);
      const endHourNum = parseInt(endHour, 10);
      
      if (!isNaN(minuteInterval) && !isNaN(startHourNum) && !isNaN(endHourNum)) {
        const startTime = `${startHourNum.toString().padStart(2, '0')}:00`;
        const endTime = `${endHourNum.toString().padStart(2, '0')}:59`;
        return {
          frequency: 'Daily',
          dailyInterval: 1,
          dailyFrequencyType: 'OccursEvery',
          dailyOccursEveryInterval: minuteInterval,
          dailyOccursEveryUnit: 'minutes',
          dailyOccursEveryStartTime: startTime,
          dailyOccursEveryEndTime: endTime,
        };
      }
    }
    
    // Check for interval patterns (every N hours)
    if (hour.startsWith('*/')) {
      const interval = parseInt(hour.substring(2), 10);
      if (!isNaN(interval)) {
        return {
          frequency: 'Daily',
          dailyInterval: 1,
          dailyTime: timeStr,
        };
      }
    }
    
    // Standard daily (Occurs At)
    return {
      frequency: 'Daily',
      dailyInterval: 1,
      dailyFrequencyType: 'OccursAt',
      dailyTime: timeStr,
    };
  }

  // Check for weekly schedule: * * * * 1 (or multiple days)
  if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*') {
    // Check for multiple days
    if (dayOfWeek.includes(',')) {
      const days = dayOfWeek.split(',').map(d => parseInt(d.trim(), 10)).filter(d => !isNaN(d) && d >= 0 && d <= 6);
      if (days.length > 0) {
        return {
          frequency: 'Weekly',
          weeklyInterval: 1,
          weeklyDays: days,
          weeklyTime: timeStr,
        };
      }
    } else if (dayOfWeek.includes('-')) {
      // Range of days
      const [start, end] = dayOfWeek.split('-').map(d => parseInt(d.trim(), 10));
      if (!isNaN(start) && !isNaN(end) && start >= 0 && start <= 6 && end >= 0 && end <= 6) {
        const days: number[] = [];
        for (let i = start; i <= end; i++) {
          days.push(i);
        }
        return {
          frequency: 'Weekly',
          weeklyInterval: 1,
          weeklyDays: days,
          weeklyTime: timeStr,
        };
      }
    } else {
      // Single day
      const dayNum = parseInt(dayOfWeek, 10);
      if (!isNaN(dayNum) && dayNum >= 0 && dayNum <= 6) {
        return {
          frequency: 'Weekly',
          weeklyInterval: 1,
          weeklyDays: [dayNum],
          weeklyTime: timeStr,
        };
      }
    }
  }

  // Check for monthly schedule: * * 15 * * (specific day of month)
  if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*') {
    const dayNum = parseInt(dayOfMonth, 10);
    if (!isNaN(dayNum) && dayNum >= 1 && dayNum <= 31) {
      return {
        frequency: 'Monthly',
        monthlyType: 'DayOfMonth',
        monthlyDay: dayNum,
        monthlyTime: timeStr,
      };
    }
  }

  // Check for monthly schedule with nth weekday: * * * * 1#2 (2nd Monday)
  if (dayOfWeek.includes('#')) {
    const [weekday, nth] = dayOfWeek.split('#');
    const weekdayNum = parseInt(weekday, 10);
    const nthNum = nth === 'L' ? -1 : parseInt(nth, 10);
    if (!isNaN(weekdayNum) && weekdayNum >= 0 && weekdayNum <= 6 && !isNaN(nthNum)) {
      return {
        frequency: 'Monthly',
        monthlyType: 'WeekdayOfMonth',
        monthlyWeekday: weekdayNum,
        monthlyWeekdayNth: nthNum,
        monthlyTime: timeStr,
      };
    }
  }

  // Check for yearly schedule with nth weekday: * * * 1 1#2 (2nd Monday of January)
  if (month !== '*' && dayOfWeek.includes('#')) {
    const [weekday, nth] = dayOfWeek.split('#');
    const weekdayNum = parseInt(weekday, 10);
    const nthNum = nth === 'L' ? -1 : parseInt(nth, 10);
    const monthNum = parseInt(month, 10);
    if (!isNaN(weekdayNum) && weekdayNum >= 0 && weekdayNum <= 6 && !isNaN(nthNum) && !isNaN(monthNum)) {
      return {
        frequency: 'Yearly',
        yearlyMonth: monthNum,
        yearlyType: 'WeekdayOfMonth',
        yearlyWeekday: weekdayNum,
        yearlyWeekdayNth: nthNum,
        yearlyTime: timeStr,
      };
    }
  }

  // Default fallback
  return {
    frequency: 'Daily',
    dailyInterval: 1,
    dailyTime: timeStr || '02:00',
  };
}
